import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Pin, Search, Info, Save, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePosStores } from "@/hooks/usePosStores";
import { useWaitlistFetcher, type WaitlistSummary } from "@/hooks/useWaitlistFetcher";
import { useFavoriteStores } from "@/hooks/useFavoriteStores";
import { useSaveSnapshot } from "@/hooks/useSaveSnapshot";
import { aggregateBrands, filterStores, brandSwatch, brandSwatchClasses, type PosStore } from "@/utils/posStores";
import { type WaitlistOrder } from "@/utils/orderSpySort";
import { fmtTime } from "@/utils/orderSpyFormat";
import { OrdersTable } from "@/components/OrdersTable";
import { OrderSpyHistory } from "@/components/OrderSpyHistory";
import { BrandTag } from "@/components/BrandTag";
import { SelectedStoreBar, EditStoreDialog } from "@/components/SelectedStoreBar";
import { AddStoreDialog } from "@/components/AddStoreDialog";

const ALL_BRANDS = "__all__";

const OrderSpy = () => {
  const { data: stores = [], isLoading: storesLoading, error: storesError } = usePosStores();
  const fetchWaitlist = useWaitlistFetcher();
  const { favorites, pinnedIds, isFavorite, toggle } = useFavoriteStores(stores);
  const saveSnapshot = useSaveSnapshot();

  // --- Store picker state -------------------------------------------------
  const [brand, setBrand] = useState<string>(ALL_BRANDS);
  const [query, setQuery] = useState<string>("");
  const [selectedStore, setSelectedStore] = useState<PosStore | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // A placeholder (blank/NOID) store being promoted to a real id via the
  // "Add ID" affordance; normal selection refuses unavailable stores.
  const [promoteStore, setPromoteStore] = useState<PosStore | null>(null);
  const comboRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeBrand = brand === ALL_BRANDS ? "" : brand;
  const brandAggregates = useMemo(() => aggregateBrands(stores), [stores]);

  const filtered = useMemo(
    () => filterStores(stores, query, activeBrand),
    [stores, query, activeBrand],
  );

  // --- Orders + fetch state -----------------------------------------------
  const [orders, setOrders] = useState<WaitlistOrder[]>([]);
  const [summary, setSummary] = useState<WaitlistSummary | null>(null);
  const [hasResult, setHasResult] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusError, setStatusError] = useState(false);

  // Monotonic token identifying the newest fetch. Bumping it both supersedes an
  // in-flight request (its result is dropped) and marks the screen as showing
  // nothing for the current selection.
  const requestIdRef = useRef(0);

  const clearResults = useCallback(() => {
    requestIdRef.current += 1;
    setFetching(false);
    setHasResult(false);
    setOrders([]);
    setSummary(null);
    setStatusMsg("");
    setStatusError(false);
  }, []);

  // Close the option list when clicking outside the combobox.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const commitStore = useCallback(
    (store: PosStore) => {
      setSelectedStore(store);
      setQuery(store.location_name);
      setListOpen(false);
      setActiveIndex(-1);
      // A different store's waitlist must never sit under this store's name
      // (spec 4.5); the operator re-fetches deliberately.
      clearResults();
    },
    [clearResults],
  );

  const clearStore = useCallback(() => {
    setSelectedStore(null);
    setQuery("");
    setActiveIndex(-1);
    setListOpen(false);
    clearResults();
    inputRef.current?.focus();
  }, [clearResults]);

  // Leave an unavailable store visible in the search list with its "Add ID"
  // affordance, instead of silently dropping it.
  const showUnavailableStore = useCallback(
    (store: PosStore) => {
      setSelectedStore(null);
      setQuery(store.location_name);
      setActiveIndex(-1);
      setListOpen(true);
      clearResults();
      inputRef.current?.focus();
    },
    [clearResults],
  );

  const onStorePromoted = useCallback(
    (updated: PosStore) => {
      setPromoteStore(null);
      if (updated.available) commitStore(updated);
      else showUnavailableStore(updated);
    },
    [commitStore, showUnavailableStore],
  );

  const onBrandChange = (value: string) => {
    setBrand(value);
    // Switching brand invalidates a pick from the old brand: it is no longer in
    // the list being searched (spec 4.1).
    if (selectedStore && value !== ALL_BRANDS && selectedStore.brand !== value) {
      setSelectedStore(null);
      setQuery("");
      clearResults();
    }
  };

  const onInputChange = (value: string) => {
    setQuery(value);
    setSelectedStore(null);
    setActiveIndex(-1);
    setListOpen(true);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // First Escape closes an open list; a second clears a committed selection.
      if (listOpen) setListOpen(false);
      else if (query) clearStore();
      return;
    }
    if (!listOpen || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (!opt) return;
      if (opt.available) commitStore(opt);
      else if (opt.id != null) {
        setListOpen(false);
        setPromoteStore(opt);
      }
    }
  };

  // --- Fetch orders -------------------------------------------------------
  const runFetch = useCallback(
    async (storeOverride?: PosStore) => {
      const store = storeOverride ?? selectedStore;
      if (!store || !store.store_id) return;

      // Triggered from a favourite chip: sync the search box + selection.
      if (storeOverride) {
        setSelectedStore(store);
        setQuery(store.location_name);
        setListOpen(false);
      }

      // Never leave a previous store's orders on screen next to a new store's
      // name (spec 4.5): clear the table and summary up front. This also
      // supersedes any request still in flight.
      clearResults();
      const requestId = requestIdRef.current;

      setFetching(true);
      setStatusMsg("Fetching…");
      setStatusError(false);

      try {
        const result = await fetchWaitlist(store.store_id);
        if (requestIdRef.current !== requestId) return;
        setOrders(result.orders);
        setSummary(result.summary);
        setHasResult(true);
        setStatusMsg(`Last updated ${new Date().toLocaleTimeString()}`);
        setStatusError(false);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setStatusMsg(err instanceof Error ? err.message : String(err));
        setStatusError(true);
        setHasResult(false);
        setOrders([]);
        setSummary(null);
      } finally {
        if (requestIdRef.current === requestId) setFetching(false);
      }
    },
    [selectedStore, fetchWaitlist, clearResults],
  );

  // Favourites group under brand headings only once more than one brand is
  // pinned; with one brand the heading is noise (spec 4.3). Favourites ignore
  // the brand filter: a cross-brand watchlist must not be hidden by a search.
  const favoritesByBrand = useMemo(() => {
    const map = new Map<string, PosStore[]>();
    for (const f of favorites) {
      const list = map.get(f.brand) ?? [];
      list.push(f);
      map.set(f.brand, list);
    }
    return map;
  }, [favorites]);

  const showResultTable = hasResult && !fetching;

  // "Save snapshot now" outcome line.
  const saveMessage = saveSnapshot.isSuccess
    ? saveSnapshot.data.message ??
      `Saved ${saveSnapshot.data.captured} store${saveSnapshot.data.captured === 1 ? "" : "s"}` +
        (saveSnapshot.data.failed > 0 ? ` (${saveSnapshot.data.failed} failed)` : "")
    : saveSnapshot.isError
      ? saveSnapshot.error.message
      : "";

  return (
    <div className="pb-10 space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">orderSpy</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Pick any store, see its live Adora POS waitlist. Pin stores to save their waitlist every
          night and compare them over time.
        </p>
      </div>

      <Tabs defaultValue="live" className="space-y-6">
        <TabsList>
          <TabsTrigger value="live">Live</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* ---------------------------------------------------------------- LIVE */}
        <TabsContent value="live" className="space-y-6 mt-0">
          {/* Store picker */}
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-foreground">Find a store</h2>
                <AddStoreDialog
                  brands={brandAggregates.map((b) => b.brand)}
                  stores={stores}
                  onCreated={(created) => {
                    setBrand(ALL_BRANDS);
                    if (created.available) commitStore(created);
                    else showUnavailableStore(created);
                  }}
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                {/* Brand filter */}
                <div className="sm:w-56">
                  <label
                    htmlFor="orderspy-brand"
                    className="block text-xs font-bold tracking-wide uppercase text-muted-foreground mb-1.5"
                  >
                    Brand
                  </label>
                  <Select value={brand} onValueChange={onBrandChange}>
                    <SelectTrigger id="orderspy-brand" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_BRANDS}>All brands</SelectItem>
                      {brandAggregates.map((b) => (
                        <SelectItem key={b.brand} value={b.brand}>
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              aria-hidden="true"
                              className={`inline-block w-2 h-2 rounded-full flex-none ${brandSwatchClasses(brandSwatch(b.brand)).dot}`}
                            />
                            {b.brand} ({b.total})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location combobox */}
                <div className="flex-1 min-w-0" ref={comboRef}>
                  <label
                    htmlFor="orderspy-location"
                    className="block text-xs font-bold tracking-wide uppercase text-muted-foreground mb-1.5"
                  >
                    Location
                  </label>
                  <div className="flex gap-2 items-start">
                    <div className="relative flex-1 min-w-0">
                      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="orderspy-location"
                        ref={inputRef}
                        role="combobox"
                        aria-expanded={listOpen}
                        aria-controls="orderspy-location-list"
                        aria-activedescendant={
                          listOpen && activeIndex >= 0 && activeIndex < filtered.length
                            ? `orderspy-location-option-${activeIndex}`
                            : undefined
                        }
                        aria-autocomplete="list"
                        autoComplete="off"
                        className="h-11 pl-9 pr-9 text-base"
                        placeholder={storesLoading ? "Loading stores…" : "Start typing to search…"}
                        value={query}
                        disabled={storesLoading || Boolean(storesError)}
                        onChange={(e) => onInputChange(e.target.value)}
                        onFocus={() => {
                          if (query.trim() && !selectedStore) setListOpen(true);
                        }}
                        onKeyDown={onInputKeyDown}
                      />
                      {query.length > 0 && (
                        <button
                          type="button"
                          aria-label="Clear selected store"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={clearStore}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}

                      {listOpen && (
                        <ul
                          id="orderspy-location-list"
                          role="listbox"
                          className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-card shadow-md"
                        >
                          {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-muted-foreground">
                              {query.trim() ? "No matching stores." : "Start typing to search…"}
                            </li>
                          ) : (
                            filtered.map((s, i) => {
                              const active = i === activeIndex;
                              return (
                                <li
                                  key={`${s.store_id}-${s.location_name}`}
                                  role="none"
                                  className={[
                                    "flex items-center justify-between gap-3 px-3 py-2 text-sm",
                                    active ? "bg-muted" : "",
                                  ].join(" ")}
                                  onMouseEnter={() => setActiveIndex(i)}
                                >
                                  <div
                                    id={`orderspy-location-option-${i}`}
                                    role="option"
                                    aria-selected={active}
                                    aria-disabled={!s.available}
                                    className={[
                                      "min-w-0 flex-1 truncate",
                                      s.available ? "cursor-pointer" : "cursor-not-allowed opacity-60",
                                    ].join(" ")}
                                    // Bind to mousedown, not click: mousedown fires before
                                    // the input blur, so the selection registers instead
                                    // of being cancelled by the list closing (spec 4.1).
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      if (s.available) commitStore(s);
                                    }}
                                  >
                                    {activeBrand ? (
                                      s.location_name
                                    ) : (
                                      <BrandTag brand={s.brand} label={s.location_name} size="xs" className="max-w-full" />
                                    )}
                                    {!s.available && (
                                      <span className="text-muted-foreground"> (unavailable)</span>
                                    )}
                                  </div>
                                  <span className="flex-none flex items-center gap-2 text-xs text-muted-foreground">
                                    {s.available ? (
                                      <span className="font-mono tabular-nums">{s.store_id}</span>
                                    ) : (
                                      s.id != null && (
                                        <button
                                          type="button"
                                          aria-label={`Add ID for ${s.location_name}`}
                                          className="cursor-pointer rounded-md border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => {
                                            setListOpen(false);
                                            setPromoteStore(s);
                                          }}
                                        >
                                          Add ID
                                        </button>
                                      )
                                    )}
                                  </span>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      )}
                    </div>
                    <Button
                      type="button"
                      className="h-11 flex-none bg-action-primary hover:bg-action-primary/90 text-primary-foreground"
                      disabled={!selectedStore || fetching}
                      onClick={() => runFetch()}
                    >
                      <RefreshCw className={`w-4 h-4 mr-1.5 ${fetching ? "animate-spin" : ""}`} />
                      {fetching ? "Fetching…" : "Fetch"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Store-id honesty note (spec 1.5): no "verified" affirmation. */}
              <div className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
                <Info className="w-4 h-4 flex-none mt-0.5" />
                <p>
                  A store id can't be verified from here: the POS returns the same empty response for
                  a valid store with no orders as it does for a typo. If the results look wrong,
                  double check the id. Correctness of the id is your call.
                </p>
              </div>

              {storesError && (
                <p className="text-sm text-status-danger">
                  Could not load the store list. Try reloading the page.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pinned stores */}
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Pin className="w-4 h-4 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Pinned stores</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11"
                    disabled={saveSnapshot.isPending || favorites.length === 0}
                    onClick={() => saveSnapshot.mutate()}
                    title="Save the current waitlist of every pinned store to history"
                  >
                    <Save
                      className={`w-4 h-4 mr-1.5 ${saveSnapshot.isPending ? "animate-pulse" : ""}`}
                    />
                    {saveSnapshot.isPending ? "Saving…" : "Save snapshot now"}
                  </Button>
                </div>
              </div>

              {saveMessage && (
                <p
                  role="status"
                  aria-live="polite"
                  className={`text-sm ${saveSnapshot.isError ? "text-status-danger" : "text-muted-foreground"}`}
                >
                  {saveMessage}
                </p>
              )}

              {favorites.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No pinned stores yet, pin the selected store below to monitor and save it here.
                </p>
              ) : favoritesByBrand.size <= 1 ? (
                <div className="flex flex-wrap gap-2">
                  {favorites.map((s) => (
                    <FavoriteChip
                      key={s.store_id}
                      store={s}
                      onFetch={runFetch}
                      onRemove={toggle}
                      fetchDisabled={fetching}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {[...favoritesByBrand.keys()]
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    .map((brandName) => (
                      <div key={brandName} className="space-y-1.5">
                        <BrandTag brand={brandName} size="xs" />
                        <div className="flex flex-wrap gap-2">
                          {favoritesByBrand.get(brandName)!.map((s) => (
                            <FavoriteChip
                              key={s.store_id}
                              store={s}
                              onFetch={runFetch}
                              onRemove={toggle}
                              fetchDisabled={fetching}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {promoteStore && promoteStore.id != null && (
            <EditStoreDialog
              key={promoteStore.id}
              store={promoteStore}
              stores={stores}
              open
              onOpenChange={(open) => {
                if (!open) setPromoteStore(null);
              }}
              onUpdated={onStorePromoted}
            />
          )}

          {/* Selected store, next to the orders below for ease of viewing
              (rather than buried in the picker card above). */}
          <SelectedStoreBar
            store={selectedStore}
            stores={stores}
            pinned={selectedStore ? isFavorite(selectedStore.store_id) : false}
            onTogglePin={() => selectedStore && toggle(selectedStore)}
            statusMsg={statusMsg}
            statusError={statusError}
            onStoreUpdated={(updated) => {
              if (!updated.available) {
                clearStore();
                return;
              }
              if (updated.store_id !== selectedStore?.store_id) {
                commitStore(updated);
                return;
              }
              setSelectedStore(updated);
              setQuery(updated.location_name);
            }}
          />

          {/* Summary stats, hidden until a successful fetch */}
          {showResultTable && summary && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryStat label="Orders" value={String(summary.order_count)} />
              <SummaryStat label="Earliest promise" value={fmtTime(summary.earliest_promise_time)} />
              <SummaryStat label="Latest promise" value={fmtTime(summary.latest_promise_time)} />
            </div>
          )}

          {/* Orders table */}
          {showResultTable &&
            (orders.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">
                  No orders on the waitlist for this store right now.
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <OrdersTable orders={orders} />
                </CardContent>
              </Card>
            ))}
        </TabsContent>

        {/* ------------------------------------------------------------- HISTORY */}
        <TabsContent value="history" className="mt-0">
          <OrderSpyHistory pinnedIds={pinnedIds} stores={stores} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface FavoriteChipProps {
  store: PosStore;
  onFetch: (store: PosStore) => void;
  onRemove: (store: PosStore) => void;
  fetchDisabled: boolean;
}

function FavoriteChip({ store, onFetch, onRemove, fetchDisabled }: FavoriteChipProps) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-background text-sm">
      <button
        type="button"
        className="flex items-center gap-1.5 pl-1.5 pr-1.5 py-1.5 rounded-l-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        title={`Fetch orders for ${store.brand}: ${store.location_name}`}
        disabled={fetchDisabled}
        onClick={() => onFetch(store)}
      >
        <BrandTag brand={store.brand} label={store.location_name} size="xs" />
      </button>
      <button
        type="button"
        aria-label={`Remove ${store.location_name} from favorites`}
        className="pr-2.5 pl-1 py-1.5 rounded-r-full text-muted-foreground hover:text-foreground hover:bg-muted"
        onClick={(e) => {
          // Removing must not also fetch (spec 4.3).
          e.stopPropagation();
          onRemove(store);
        }}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs font-bold tracking-wide uppercase text-muted-foreground">
          {label}
        </div>
        <div className="text-2xl font-bold text-foreground tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

export default OrderSpy;
